import React, { useEffect, useState, useCallback } from 'react';
import { Bell, BellOff, BellRing, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  pushSupported, currentPushPermission, getExistingSubscription,
  enablePush, disablePush, sendTestPush,
} from '@/lib/webpush';

/**
 * Compact toggle for browser Push notifications, sized to sit in the header.
 *
 * States (traffic-light UX):
 *   - unsupported    → grey, disabled, tooltip explains why
 *   - denied         → amber, prompts the user to unblock in browser settings
 *   - not-subscribed → white with Bell icon, click activates
 *   - subscribed     → green with BellRing icon, click un-subscribes (Alt+click sends test)
 *
 * The button is polling-free: `subscribed` is only re-checked on mount and
 * after every explicit toggle. Simpler than watching pushManager and works
 * because subscriptions can't disappear without our action.
 */
export const PushToggleButton = ({ className = '' }) => {
  const [supported] = useState(pushSupported());
  const [permission, setPermission] = useState(currentPushPermission());
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!supported) return;
    const sub = await getExistingSubscription();
    setSubscribed(!!sub);
    setPermission(currentPushPermission());
  }, [supported]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleClick = async (e) => {
    if (busy) return;
    if (!supported) return;

    // Alt-click = test push (only when already subscribed)
    if (e.altKey && subscribed) {
      setBusy(true);
      try {
        const res = await sendTestPush();
        toast.success(`Push de teste enviado (${res.sent} entregue)`);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Falha ao enviar push de teste');
      } finally { setBusy(false); }
      return;
    }

    setBusy(true);
    try {
      if (subscribed) {
        await disablePush();
        toast.success('Notificações push desligadas neste dispositivo');
      } else {
        const res = await enablePush();
        if (!res.ok) {
          toast.error(res.message || 'Não foi possível ativar notificações');
        } else {
          toast.success('Notificações push ativas — receberá alertas críticos em tempo real');
        }
      }
      await refresh();
    } catch (err) {
      toast.error(err.message || 'Erro ao alterar preferências de notificações');
    } finally {
      setBusy(false);
    }
  };

  if (!supported) {
    return (
      <Button
        variant="ghost" size="sm" disabled
        className={`text-slate-500 ${className}`}
        title="Notificações push não são suportadas neste browser"
        data-testid="push-toggle-btn"
      >
        <BellOff className="w-4 h-4 mr-2" /> Push indisponível
      </Button>
    );
  }

  if (permission === 'denied') {
    return (
      <Button
        variant="ghost" size="sm"
        className={`text-amber-300 hover:bg-amber-900/20 ${className}`}
        title="Notificações bloqueadas nas definições do browser — desbloqueie e recarregue"
        onClick={() => toast.warning('As notificações estão bloqueadas nas definições do browser. Desbloqueie e recarregue a página.')}
        data-testid="push-toggle-btn"
      >
        <AlertCircle className="w-4 h-4 mr-2" /> Bloqueado
      </Button>
    );
  }

  return (
    <Button
      variant="ghost" size="sm"
      onClick={handleClick}
      disabled={busy}
      className={
        subscribed
          ? `text-emerald-300 hover:bg-emerald-900/20 ${className}`
          : `text-slate-300 hover:text-white hover:bg-white/5 ${className}`
      }
      title={subscribed
        ? 'Notificações ativas · Alt+clique para enviar teste · Clique para desativar'
        : 'Ativar notificações push (alertas críticos em tempo real)'}
      data-testid="push-toggle-btn"
    >
      {busy
        ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        : subscribed
          ? <BellRing className="w-4 h-4 mr-2" />
          : <Bell className="w-4 h-4 mr-2" />}
      {subscribed ? 'Notificações' : 'Ativar push'}
    </Button>
  );
};
